import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Mail, Loader2, Star, ShieldCheck, AlertCircle, Key } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card.js';
import { Button } from '@/components/ui/Button.js';
import { useAuth } from '@/lib/auth.js';
import { api, ApiError, type AccountEmail } from '@/api/client.js';
import { cn } from '@/lib/utils.js';
import { TwoFactorEnrollDialog } from '@/components/account/TwoFactorEnrollDialog.js';

export function AccountPage(): JSX.Element {
  const { user, refresh } = useAuth();
  const queryClient = useQueryClient();
  const emailsQuery = useQuery({ queryKey: ['account-emails'], queryFn: () => api.account.emails() });
  const twoFactorQuery = useQuery({
    queryKey: ['two-factor-status'],
    queryFn: () => api.auth.twoFactor.status(),
  });
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [regenInFlight, setRegenInFlight] = useState(false);
  const [disableInFlight, setDisableInFlight] = useState(false);

  async function regenerate() {
    if (!confirm('Regenerating invalidates your current recovery codes immediately. Continue?')) return;
    setRegenInFlight(true);
    try {
      const { recoveryCodes } = await api.auth.twoFactor.regenerateRecoveryCodes();
      const text = `${recoveryCodes.join('\n')}\n`;
      await navigator.clipboard.writeText(text);
      toast.success('New recovery codes copied. The previous set is now invalid.');
      await queryClient.invalidateQueries({ queryKey: ['two-factor-status'] });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not regenerate codes.');
    } finally {
      setRegenInFlight(false);
    }
  }

  async function disable2fa() {
    const code = window.prompt('Enter a current 6-digit code from your authenticator to confirm:');
    if (!code) return;
    setDisableInFlight(true);
    try {
      await api.auth.twoFactor.disable({ code });
      toast.success('Two-factor authentication disabled.');
      await queryClient.invalidateQueries({ queryKey: ['two-factor-status'] });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not disable 2FA.');
    } finally {
      setDisableInFlight(false);
    }
  }

  async function makePrimary(id: string) {
    try {
      await api.account.setPrimary(id);
      toast.success('Primary email updated');
      await queryClient.invalidateQueries({ queryKey: ['account-emails'] });
      await refresh();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not update primary email');
    }
  }

  return (
    <div className="space-y-8">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Account</h1>
        <p className="text-sm text-muted-foreground">
          Your identity. Memberships use these email addresses to know how to reach you per org.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Profile</CardTitle>
          <CardDescription>One identity, many memberships.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          {user && (
            <>
              <Row label="Name" value={user.name} />
              <Row label="Primary email" value={user.email} />
              <Row label="User id" value={user.id} mono />
              <Row label="Member since" value={new Date(user.createdAt).toLocaleString()} />
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-4 w-4" />
            Email addresses
          </CardTitle>
          <CardDescription>
            Different orgs can reach you at different addresses. Promote any verified address to
            primary. New emails arrive when you accept invitations sent to them.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {emailsQuery.isLoading ? (
            <div className="flex justify-center py-4 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {(emailsQuery.data ?? []).map((email) => (
                <EmailRow key={email.id} email={email} onMakePrimary={() => makePrimary(email.id)} />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="h-4 w-4" />
            Two-factor authentication
          </CardTitle>
          <CardDescription>
            A 6-digit code from an authenticator app, every time you sign in with a password. Adds
            a second factor on top of your password so a leaked password isn't enough by itself.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {twoFactorQuery.isLoading ? (
            <div className="flex justify-center py-4 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
            </div>
          ) : twoFactorQuery.data?.enabled ? (
            <>
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-status-up/40 bg-status-up/5 px-3 py-2 text-sm">
                <div>
                  <span className="inline-flex items-center gap-1 font-medium text-status-up">
                    <ShieldCheck className="h-4 w-4" />
                    Enabled
                  </span>
                  {twoFactorQuery.data.enabledAt && (
                    <span className="ml-2 text-xs text-muted-foreground">
                      since {new Date(twoFactorQuery.data.enabledAt).toLocaleDateString()}
                    </span>
                  )}
                </div>
                <div className="text-xs text-muted-foreground">
                  {twoFactorQuery.data.recoveryCodesRemaining} recovery code
                  {twoFactorQuery.data.recoveryCodesRemaining === 1 ? '' : 's'} remaining
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" onClick={regenerate} disabled={regenInFlight}>
                  {regenInFlight ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Regenerate recovery codes'}
                </Button>
                <Button variant="ghost" size="sm" onClick={disable2fa} disabled={disableInFlight}>
                  {disableInFlight ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Disable 2FA'}
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-900 dark:text-amber-200">
                Not enabled. Strongly recommended — set it up now and keep the recovery codes in a
                safe place.
              </div>
              <Button onClick={() => setEnrollOpen(true)}>Enable two-factor authentication</Button>
            </>
          )}
        </CardContent>
      </Card>

      <TwoFactorEnrollDialog
        open={enrollOpen}
        onOpenChange={setEnrollOpen}
        onActivated={() => {
          void queryClient.invalidateQueries({ queryKey: ['two-factor-status'] });
        }}
      />
    </div>
  );
}

function EmailRow({
  email,
  onMakePrimary,
}: {
  email: AccountEmail;
  onMakePrimary: () => void;
}): JSX.Element {
  return (
    <li className="flex items-center justify-between gap-3 py-3">
      <div className="flex items-center gap-3">
        <Mail className="h-4 w-4 text-muted-foreground" />
        <div>
          <div className="font-mono text-sm">{email.email}</div>
          <div className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
            <span>Added via {email.source}</span>
            {email.verified ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-status-up/10 px-1.5 py-0.5 text-[10px] text-status-up">
                <ShieldCheck className="h-2.5 w-2.5" />
                verified
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 rounded-full bg-status-degraded/10 px-1.5 py-0.5 text-[10px] text-status-degraded">
                <AlertCircle className="h-2.5 w-2.5" />
                unverified
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {email.isPrimary ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-primary">
            <Star className={cn('h-3 w-3', 'fill-primary')} />
            primary
          </span>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            disabled={!email.verified}
            onClick={onMakePrimary}
            title={!email.verified ? 'Verify the email before promoting' : undefined}
          >
            Make primary
          </Button>
        )}
      </div>
    </li>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }): JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <span className="text-xs uppercase tracking-wider text-muted-foreground">{label}</span>
      <span className={mono ? 'font-mono text-xs' : ''}>{value}</span>
    </div>
  );
}
